import { batch } from 'editor/src/store/batchedSubscribeEnhancer';
import type { Thunk } from 'editor/src/store/hooks';
import getAllMatchingVariations from 'editor/src/store/variants/helpers/getAllMatchingVariations';
import changeEcommerceVariantPageCountOperation from 'editor/src/store/variants/operation/changeEcommerceVariantPageCountOperation';
import checkForMissingTemplatesOperation from 'editor/src/store/variants/operation/checkForMissingTemplatesOperation';
import getECommercePageCounts from 'editor/src/store/variants/selector/getECommercePageCounts';
import { setPageCountAction } from 'editor/src/store/variants/slice';

import sendPostMessage from 'editor/src/util/postMessages/sendPostMessage';

import saveUndoRedoStateOperation from '../../../editorModules/undoRedo/operation/saveUndoRedoStateOperation';
import { copySpreadAction } from '../../slice';
import { DesignData } from '../../types';
import getSpreadCountForIncrement from '../../util/getSpreadCountForIncrement';

// Resolves which spread to copy from: the clicked spread when it is a template,
// otherwise the first template spread in the design.
function resolveTemplateIndex(designData: DesignData, spreadIndex: number): number {
  if (designData.spreads[spreadIndex]?.isTemplate) {
    return spreadIndex;
  }
  const index = designData.spreads.findIndex((spread) => spread.isTemplate);
  if (index === -1) {
    throw new Error('No template spread found');
  }
  return index;
}

const addSpreadOperation =
  (spreadIndex: number, duplicate = false): Thunk =>
  (dispatch, getState) => {
    let state = getState();
    const { designData } = state.design;
    if (!designData) {
      return;
    }

    batch(() => {
      // Advance page_count to the next valid grid value: adding one page-set may
      // mean inserting several spreads (e.g. 4 single-page spreads for a brochure
      // printed in 4-page signatures, or 2 two-page spreads for a 4-page photobook
      // step), otherwise the resulting page count lands off the valid grid and the
      // product cannot switch to it.
      const spreadsToAdd = getSpreadCountForIncrement(
        designData,
        resolveTemplateIndex(designData, spreadIndex),
        getECommercePageCounts(state),
      );

      dispatch(saveUndoRedoStateOperation('add spread'));

      for (let i = 0; i < spreadsToAdd; i += 1) {
        const currentData = getState().design.designData;
        if (!currentData) {
          break;
        }
        dispatch(
          copySpreadAction({
            copyFromIndex: resolveTemplateIndex(currentData, spreadIndex),
            positionAtIndex: spreadIndex + 1 + i,
            empty: !duplicate,
            mediaFromIndex: spreadIndex,
          }),
        );
      }

      state = getState();
      if (state.design.designData?.page_count) {
        dispatch(setPageCountAction(state.design.designData.page_count));
        dispatch(changeEcommerceVariantPageCountOperation(state.design.designData.page_count));
        sendPostMessage('design.switchToProduct', {
          productUid: state.design.designData.product_uid,
          pageCount: state.design.designData.page_count,
        });

        state = getState();
        const { product, configuration, selectedMultiOptions, selectedSingleOptions, replaceControlKeys } =
          state.variants;
        if (!configuration.singleSelection) {
          const matchingVariations = getAllMatchingVariations(
            product.productVariations,
            selectedMultiOptions,
            selectedSingleOptions,
            false,
            replaceControlKeys,
          );
          dispatch(checkForMissingTemplatesOperation(matchingVariations));
        }
      }
    });
  };

export default addSpreadOperation;
